import { useReducer, useEffect, useCallback } from 'react'

const actionTypes = {
  setTuneValue: 'SET_TUNE_VALUE',
  setRulerValue: 'SET_RULER_VALUE',
  setInputValue: 'SET_INPUT_VALUE',
  setInputFocused: 'SET_INPUT_FOCUSED'
}

const tuneValueReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.setTuneValue: {
      return {
        ...state,
        tuneValue: action.tuneValue
      }
    }
    case actionTypes.setRulerValue: {
      return {
        ...state,
        rulerValue: action.rulerValue
      }
    }
    case actionTypes.setInputValue: {
      return {
        ...state,
        inputValue: action.inputValue
      }
    }
    case actionTypes.setInputFocused: {
      return {
        ...state,
        inputFocused: action.inputFocused
      }
    }
    default: {
      throw new Error(`Unhandled type ${action.type}`)
    }
  }
}

export const useTuneValue = ({ reducer = tuneValueReducer, price, bounds, useStep } = {}) => {
  const initialState = {
    tuneValue: price,
    rulerValue: price,
    inputValue: null,
    inputFocused: false
  }

  const [{ tuneValue, rulerValue, inputValue, inputFocused }, dispatch] = useReducer(reducer, initialState)

  const usePins = [
    {
      type: 'homebot',
      amount: Math.round(bounds.homebotAmount / useStep) * useStep
    }
  ]
  if (bounds.customerAmount) {
    usePins.push({
      type: 'customer',
      amount: Math.round(bounds.customerAmount / useStep) * useStep,
      photoUri: bounds.customerPhotoUri
    })
  }

  const useMin = Math.ceil(bounds.low / useStep) * useStep
  const useMax = Math.floor(bounds.high / useStep) * useStep

  const setTuneValue = useCallback(
    tuneValue => {
      dispatch({
        type: actionTypes.setTuneValue,
        tuneValue
      })
    },
    [dispatch]
  )

  const setRulerValue = useCallback(
    rulerValue => {
      dispatch({
        type: actionTypes.setRulerValue,
        rulerValue
      })
    },
    [dispatch]
  )

  const setInputValue = useCallback(
    inputValue => {
      dispatch({
        type: actionTypes.setInputValue,
        inputValue
      })
    },
    [dispatch]
  )

  const setInputFocused = useCallback(
    inputFocused => {
      dispatch({
        type: actionTypes.setInputFocused,
        inputFocused
      })
    },
    [dispatch]
  )

  const getValueFromEvent = useCallback(
    evt => {
      let value
      try {
        value = evt.target.value
        value = value.replace(/\.\d+$/, '').replace(/[^\d]/g, '')
        value = parseInt(value, 10)
        value = Math.max(useMin, Math.min(useMax, value))
      } catch (err) {
        console.warn('getValueFromEvent failed to parse, returning `undefined`:', err)
      }
      return value && !isNaN(value) ? value : undefined
    },
    [useMin, useMax]
  )

  useEffect(() => {
    const rounded = Math.round(tuneValue / useStep) * useStep
    if (inputValue === null && rounded === rulerValue) {
      setInputValue(tuneValue)
    }
  }, [tuneValue, inputValue, rulerValue, useStep, setInputValue])

  return {
    tuneValue,
    rulerValue,
    inputValue,
    inputFocused,
    setTuneValue,
    setRulerValue,
    setInputValue,
    setInputFocused,
    useStep,
    useMin,
    useMax,
    usePins,
    getValueFromEvent
  }
}
